/**
 * FaderFleet Shopify Theme JavaScript
 * Handles interactive features and dashboard functionality
 */

(function() {
  'use strict';

  // ============================================
  // Mobile Navigation
  // ============================================
  function initMobileNav() {
    const toggle = document.querySelector('.mobile-menu-toggle');
    const navList = document.querySelector('.nav-list');

    if (!toggle || !navList) return;

    toggle.addEventListener('click', function() {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', !isExpanded);
      navList.classList.toggle('active');
    });

    // Close menu when clicking outside
    document.addEventListener('click', function(e) {
      if (!toggle.contains(e.target) && !navList.contains(e.target)) {
        toggle.setAttribute('aria-expanded', 'false');
        navList.classList.remove('active');
      }
    });
  }

  // ============================================
  // FAQ Accordion
  // ============================================
  function initFaqAccordion() {
    const faqItems = document.querySelectorAll('.faq-item');

    faqItems.forEach(item => {
      const question = item.querySelector('.faq-question');

      if (!question) return;

      question.addEventListener('click', function() {
        const isActive = item.classList.contains('active');

        // Close all other items
        faqItems.forEach(otherItem => {
          if (otherItem !== item) {
            otherItem.classList.remove('active');
            otherItem.querySelector('.faq-question')?.setAttribute('aria-expanded', 'false');
          }
        });

        // Toggle current item
        item.classList.toggle('active');
        question.setAttribute('aria-expanded', !isActive);
      });
    });
  }

  // ============================================
  // Smooth Scroll for Anchor Links
  // ============================================
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const targetElement = document.querySelector(targetId);
        if (!targetElement) return;

        e.preventDefault();
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      });
    });
  }

  // ============================================
  // Dashboard Subscription Manager
  // ============================================
  const SubscriptionManager = {
    storageKey: 'faderfleet_subscription',

    // Get subscription data from localStorage or defaults
    getData() {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.warn('Error parsing subscription data:', e);
        }
      }

      // Return default data - in production, this would come from your subscription system
      return this.getDefaultData();
    },

    getDefaultData() {
      // This should be populated by your subscription management system
      // (Recharge, Bold, Shopify Subscriptions, or custom metafields)
      return {
        planName: 'EP Plan',
        totalCredits: 4,
        usedCredits: 0,
        billingDate: this.getNextBillingDate(),
        price: '$120'
      };
    },

    getNextBillingDate() {
      const date = new Date();
      date.setMonth(date.getMonth() + 1);
      date.setDate(1);
      return date.toISOString();
    },

    // Save subscription data
    saveData(data) {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    },

    // Use a credit
    useCredit() {
      const data = this.getData();
      if (data.usedCredits < data.totalCredits) {
        data.usedCredits++;
        this.saveData(data);
        return true;
      }
      return false;
    },

    // Reset credits (called at start of new billing cycle)
    resetCredits() {
      const data = this.getData();
      data.usedCredits = 0;
      data.billingDate = this.getNextBillingDate();
      this.saveData(data);
    },

    // Get remaining credits
    getRemainingCredits() {
      const data = this.getData();
      return data.totalCredits - data.usedCredits;
    },

    // Check if credits are available
    hasCredits() {
      return this.getRemainingCredits() > 0;
    }
  };

  // ============================================
  // Dashboard Initialization
  // ============================================
  function initDashboard() {
    const dashboard = document.querySelector('.customer-dashboard');
    if (!dashboard) return;

    const data = SubscriptionManager.getData();

    // Update credits display
    updateCreditsDisplay(data);

    // Update billing info
    updateBillingInfo(data);

    // Update project stats
    updateProjectStats();

    // Handle submit button state
    updateSubmitButton(data);

    // Initialize cancel subscription handler
    initCancelSubscription();
  }

  function updateCreditsDisplay(data) {
    const remaining = data.totalCredits - data.usedCredits;
    const percentage = (remaining / data.totalCredits) * 100;

    // Update text values
    const creditsRemaining = document.getElementById('credits-remaining');
    const totalCredits = document.getElementById('total-credits');
    const usedCredits = document.getElementById('used-credits');
    const planName = document.getElementById('plan-name');

    if (creditsRemaining) creditsRemaining.textContent = remaining;
    if (totalCredits) totalCredits.textContent = data.totalCredits;
    if (usedCredits) usedCredits.textContent = data.usedCredits;
    if (planName) planName.textContent = data.planName;

    // Update ring progress
    const progressRing = document.getElementById('credits-progress');
    if (progressRing) {
      const circumference = 2 * Math.PI * 54;
      const offset = circumference - (percentage / 100) * circumference;
      progressRing.style.strokeDasharray = circumference;
      progressRing.style.strokeDashoffset = offset;
    }
  }

  function updateBillingInfo(data) {
    const billingDate = new Date(data.billingDate);
    const now = new Date();
    const daysRemaining = Math.ceil((billingDate - now) / (1000 * 60 * 60 * 24));

    const daysRemainingEl = document.getElementById('days-remaining');
    const nextBillingEl = document.getElementById('next-billing');
    const currentPlanEl = document.getElementById('current-plan-name');
    const planPriceEl = document.getElementById('plan-price');

    if (daysRemainingEl) daysRemainingEl.textContent = Math.max(0, daysRemaining);
    if (nextBillingEl) {
      nextBillingEl.textContent = billingDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
    if (currentPlanEl) currentPlanEl.textContent = data.planName;
    if (planPriceEl) planPriceEl.textContent = data.price + '/mo';
  }

  function updateProjectStats() {
    const projectItems = document.querySelectorAll('.project-item');
    let completed = 0;
    let inProgress = 0;

    projectItems.forEach(item => {
      const status = item.querySelector('.status-badge');
      if (status) {
        if (status.classList.contains('status-completed')) completed++;
        else inProgress++;
      }
    });

    const completedEl = document.getElementById('completed-projects');
    const inProgressEl = document.getElementById('in-progress');

    if (completedEl) completedEl.textContent = completed;
    if (inProgressEl) inProgressEl.textContent = inProgress;
  }

  function updateSubmitButton(data) {
    const submitBtn = document.getElementById('submit-btn');
    if (!submitBtn) return;

    const remaining = data.totalCredits - data.usedCredits;

    if (remaining <= 0) {
      submitBtn.classList.add('disabled');
      submitBtn.textContent = 'No Credits Remaining';
      submitBtn.removeAttribute('href');
    }
  }

  function initCancelSubscription() {
    const cancelBtn = document.getElementById('cancel-subscription');
    if (!cancelBtn) return;

    cancelBtn.addEventListener('click', function(e) {
      e.preventDefault();

      const confirmed = confirm(
        'Are you sure you want to cancel your subscription?\n\n' +
        'You will retain access until the end of your current billing period.'
      );

      if (confirmed) {
        // In production, this would integrate with your subscription system
        alert('Please contact faderfleet@gmail.com to cancel your subscription.');
      }
    });
  }

  // ============================================
  // Waveform Animation
  // ============================================
  function initWaveformAnimation() {
    const waveformBars = document.querySelectorAll('.wave-bar');
    if (waveformBars.length === 0) return;

    waveformBars.forEach((bar, index) => {
      const randomHeight = 20 + Math.random() * 80;
      const randomDelay = index * 0.1;
      bar.style.setProperty('--delay', randomDelay + 's');
    });
  }

  // ============================================
  // Pricing Card Hover Effects
  // ============================================
  function initPricingCards() {
    const pricingCards = document.querySelectorAll('.pricing-card');

    pricingCards.forEach(card => {
      card.addEventListener('mouseenter', function() {
        pricingCards.forEach(c => {
          if (c !== card && !c.classList.contains('featured')) {
            c.style.opacity = '0.7';
          }
        });
      });

      card.addEventListener('mouseleave', function() {
        pricingCards.forEach(c => {
          c.style.opacity = '1';
        });
      });
    });
  }

  // ============================================
  // Form Validation Enhancement
  // ============================================
  function initFormValidation() {
    const forms = document.querySelectorAll('form');

    forms.forEach(form => {
      const inputs = form.querySelectorAll('.form-input');

      inputs.forEach(input => {
        input.addEventListener('blur', function() {
          validateInput(this);
        });

        input.addEventListener('input', function() {
          if (this.classList.contains('invalid')) {
            validateInput(this);
          }
        });
      });

      form.addEventListener('submit', function(e) {
        let isValid = true;

        inputs.forEach(input => {
          if (!validateInput(input)) {
            isValid = false;
          }
        });

        if (!isValid) {
          e.preventDefault();
        }
      });
    });
  }

  function validateInput(input) {
    const value = input.value.trim();
    let isValid = true;

    // Remove existing error state
    input.classList.remove('invalid');

    // Required validation
    if (input.required && !value) {
      isValid = false;
    }

    // Email validation
    if (input.type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        isValid = false;
      }
    }

    // Min length validation
    if (input.minLength && value.length < input.minLength) {
      isValid = false;
    }

    if (!isValid) {
      input.classList.add('invalid');
    }

    return isValid;
  }

  // ============================================
  // Lazy Loading Images
  // ============================================
  function initLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            observer.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  // ============================================
  // Scroll Animations
  // ============================================
  function initScrollAnimations() {
    if ('IntersectionObserver' in window) {
      const animateObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      document.querySelectorAll(
        '.service-card, .step-card, .pricing-card, .benefit-card, .requirement-card'
      ).forEach(el => {
        el.classList.add('animate-ready');
        animateObserver.observe(el);
      });
    }
  }

  // ============================================
  // Initialize Everything
  // ============================================
  function init() {
    initMobileNav();
    initFaqAccordion();
    initSmoothScroll();
    initDashboard();
    initWaveformAnimation();
    initPricingCards();
    initFormValidation();
    initLazyLoading();
    initScrollAnimations();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose SubscriptionManager for external use
  window.FaderFleet = {
    SubscriptionManager: SubscriptionManager
  };

})();
